import { commandLabel } from '../components/terminal/commandNotify';

/**
 * Where each prompt starts in a terminal's buffer (Terminal board card 3,
 * 21.10.2026), for "Copy last output". Shells mark a prompt with OSC 133;A
 * (our own prompts on Windows — terminal.rs — and any shell with shell
 * integration). A marker per prompt follows its line as the buffer scrolls;
 * xterm disposes markers whose lines are trimmed off the scrollback.
 *
 * Command timing (card 5): Enter at a prompt starts a command, the next
 * prompt mark ends it; `onCommandDone(fn)` gets `{ command, ms }`.
 *
 * Its own OSC handler, registered after the divider handler and returning
 * false, so the dividers keep working.
 */
export function trackPromptMarks(term, now = () => Date.now()) {
  const marks = [];
  const listeners = new Set();
  let running = null; // { command, at }

  term.parser.registerOscHandler(133, (data) => {
    if (typeof data === 'string' && data.startsWith('A')) {
      const marker = term.registerMarker(0);
      if (marker) marks.push(marker);
      if (running) {
        const done = { command: running.command, ms: now() - running.at };
        running = null;
        listeners.forEach((fn) => fn(done));
      }
    }
    return false;
  });

  // Only after a prompt was seen: without marks there is no end to measure.
  term.onData?.((data) => {
    if (running || !marks.length || !String(data).includes('\r')) return;
    const b = term.buffer.active;
    const y = b.baseY + b.cursorY;
    const text = b.getLine(y)?.translateToString(true) || '';
    const last = marks[marks.length - 1];
    const command = commandLabel(text, !!last && last.line === y);
    if (command) running = { command, at: now() };
  });

  return {
    /** Buffer line of every prompt still in the scrollback, oldest first. */
    lines: () => marks.filter((m) => !m.isDisposed && m.line >= 0).map((m) => m.line),
    /** A screen clear: the old prompts are gone with the text. */
    clear: () => {
      marks.splice(0).forEach((m) => m.dispose?.());
    },
    /** Subscribe to finished commands; returns the unsubscribe. */
    onCommandDone: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
